import { bytesToHex } from '@noble/hashes/utils'

import {
  CHAIN_CONSTANTS,
  SAFETY_CONFIG,
  CPN_EIP712_TYPES,
} from '../common/constants'

import type {
  ValidationInput,
  EIP712SigningPayload,
  EIP712TypedData,
  TestnetChain,
} from '../../types'

/**
 * Generates a signing payload for an EIP-712 transaction.
 */
export function generateEIP712SigningPayload(
  input: ValidationInput,
): EIP712SigningPayload {
  const constants = CHAIN_CONSTANTS[input.chain]
  const expiredTimestamp = SAFETY_CONFIG.getExpiredTimestamp()

  const typedData = {
    domain: {
      name: 'USDC',
      version: '2',
      chainId: parseInt(constants.chainId, 10),
      verifyingContract: constants.usdcContract as `0x${string}`,
    },
    types: CPN_EIP712_TYPES,
    primaryType: 'TransferWithAuthorization' as const,
    message: {
      from: input.walletAddress as `0x${string}`,
      to: SAFETY_CONFIG.BURN_ADDRESS_EVM as `0x${string}`,
      value: SAFETY_CONFIG.TEST_AMOUNT,
      validAfter: '0',
      validBefore: expiredTimestamp.toString(),
      nonce: generateSafeNonce() as `0x${string}`,
    },
  } as const

  return {
    chain: input.chain as
      | TestnetChain.EthereumSepolia
      | TestnetChain.ArbitrumSepolia
      | TestnetChain.BaseSepolia,
    walletAddress: input.walletAddress,
    payload: typedData as EIP712TypedData,
  }
}

function generateSafeNonce(): string {
  // Use crypto API that works in both browser and Node.js environments
  let crypto: Crypto
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (typeof globalThis !== 'undefined' && globalThis.crypto) {
    // Browser environment
    crypto = globalThis.crypto
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  } else if (typeof window !== 'undefined' && window.crypto) {
    // Browser environment fallback
    crypto = window.crypto
  } else {
    // Node.js environment
    // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    crypto = require('crypto').webcrypto
  }

  const randomBytes = crypto.getRandomValues(new Uint8Array(32))
  return '0x' + bytesToHex(randomBytes)
}
